import { useState } from 'react';

interface VehicleDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicle: any;
}

export default function VehicleDetailsModal({ isOpen, onClose, vehicle }: VehicleDetailsModalProps) {
  const [selectedPaymentType, setSelectedPaymentType] = useState<'full' | 'deposit' | 'custom'>('full');
  const [customAmount, setCustomAmount] = useState('');
  const [customerInfo, setCustomerInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  // Calculate Quebec taxes at the top so they're available everywhere
  const gst = vehicle?.price ? vehicle.price * 0.05 : 0;
  const qst = vehicle?.price ? vehicle.price * 0.09975 : 0;
  const totalTaxes = gst + qst;
  const totalWithTaxes = vehicle?.price ? vehicle.price + totalTaxes : 0;
  const depositAmount = vehicle?.price ? vehicle.price * 0.1 : 0;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setCustomAmount(value);
  };

  const getPaymentAmount = () => {
    if (selectedPaymentType === 'full') {
      return Math.round(totalWithTaxes);
    } else if (selectedPaymentType === 'deposit') {
      return Math.round(depositAmount);
    } else if (selectedPaymentType === 'custom') {
      return Number(customAmount) || 0;
    }
    return 0;
  };

  const handlePayment = async () => {
    console.log('=== PAYMENT DEBUG START ===');
    console.log('Payment type:', selectedPaymentType);
    console.log('Vehicle:', vehicle);
    console.log('Customer info:', customerInfo);
    console.log('Custom amount:', customAmount);
    console.log('Payment amount:', getPaymentAmount());

    if (!customerInfo.firstName || !customerInfo.lastName || !customerInfo.email || !customerInfo.phone) {
      alert('Please fill in all customer information fields');
      return;
    }

    if (!vehicle || !vehicle.id || !vehicle.price) {
      alert('Vehicle information is missing');
      return;
    }

    if (selectedPaymentType === 'custom') {
      const amount = Number(customAmount);
      if (!amount || amount <= 0) {
        alert('Please enter a valid payment amount');
        return;
      }
      if (amount > totalWithTaxes) {
        alert('Payment amount cannot exceed the total vehicle price with taxes');
        return;
      }
    }

    setIsProcessingPayment(true);

    try {
      const paymentAmount = getPaymentAmount();
      const paymentData = {
        vehicleId: String(vehicle.id),
        vehicleTitle: String(vehicle.title || 'Vehicle'),
        vehiclePrice: Number(vehicle.price),
        customerInfo: {
          firstName: String(customerInfo.firstName),
          lastName: String(customerInfo.lastName),
          email: String(customerInfo.email),
          phone: String(customerInfo.phone),
          name: `${customerInfo.firstName} ${customerInfo.lastName}`
        },
        paymentType: selectedPaymentType,
        customAmount: selectedPaymentType === 'custom' ? paymentAmount : undefined,
        amount: paymentAmount,
      };

      console.log('Sending payment data:', paymentData);

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-vehicle-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(paymentData),
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Payment response:', result);

      if (result.success && result.url) {
        console.log('SUCCESS! Redirecting to:', result.url);
        
        // Create a temporary anchor element and click it - this bypasses popup blockers
        const link = document.createElement('a');
        link.href = result.url;
        link.target = '_self';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Backup method - direct assignment
        setTimeout(() => {
          window.location.href = result.url;
        }, 100);
        
      } else {
        console.error('Payment failed - no URL returned:', result);
        alert(`Payment failed: ${result.error || 'Unknown error'}`);
        setIsProcessingPayment(false);
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert(`Payment failed: ${error.message}`);
      setIsProcessingPayment(false);
    }
    
    console.log('=== PAYMENT DEBUG END ===');
  };

  if (!isOpen || !vehicle) return null;

  // Prepare all images (main image + gallery images)
  const allImages = [];
  if (vehicle.image_url || vehicle.image) {
    allImages.push(vehicle.image_url || vehicle.image);
  }
  if (vehicle.gallery_images && vehicle.gallery_images.length > 0) {
    allImages.push(...vehicle.gallery_images);
  }

  // Remove duplicates
  const uniqueImages = [...new Set(allImages)];

  const openLightbox = () => {
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  const nextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % uniqueImages.length);
  };

  const prevImage = () => {
    setSelectedImageIndex((prev) => (prev - 1 + uniqueImages.length) % uniqueImages.length);
  };

  const handleLightboxKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeLightbox();
    } else if (e.key === 'ArrowRight') {
      nextImage();
    } else if (e.key === 'ArrowLeft') {
      prevImage();
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">{vehicle.title}</h2>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Vehicle Details */}
              <div>
                {/* Media Section */}
                <div className="mb-6">
                  {!showVideo || !vehicle.video_url ? (
                    <div className="space-y-4">
                      {/* Main Image Display */}
                      <div className="relative">
                        <img 
                          src={uniqueImages[selectedImageIndex] || vehicle.image_url || vehicle.image} 
                          alt={vehicle.title}
                          className="w-full h-64 object-contain bg-gray-100 rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={openLightbox}
                        />
                        
                        {/* Zoom Icon Overlay */}
                        <div className="absolute top-4 left-4 bg-black/70 text-white px-2 py-1 rounded-lg text-sm flex items-center gap-1">
                          <i className="ri-zoom-in-line"></i>
                          <span>Click to enlarge</span>
                        </div>

                        {vehicle.video_url && (
                          <button
                            onClick={() => setShowVideo(true)}
                            className="absolute top-4 right-4 bg-black/70 text-white px-3 py-2 rounded-lg hover:bg-black/80 transition-colors cursor-pointer flex items-center gap-2"
                          >
                            <i className="ri-play-fill"></i>
                            <span className="text-sm">Play Video</span>
                          </button>
                        )}
                        
                        {/* Image Counter */}
                        {uniqueImages.length > 1 && (
                          <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                            {selectedImageIndex + 1} / {uniqueImages.length}
                          </div>
                        )}
                      </div>

                      {/* Image Gallery Thumbnails */}
                      {uniqueImages.length > 1 && (
                        <div className="flex gap-2 overflow-x-auto pb-2">
                          {uniqueImages.map((imageUrl, index) => (
                            <button
                              key={index}
                              onClick={() => setSelectedImageIndex(index)}
                              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors cursor-pointer ${
                                selectedImageIndex === index 
                                  ? 'border-blue-600' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <img 
                                src={imageUrl} 
                                alt={`${vehicle.title} view ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="relative">
                      <video 
                        src={vehicle.video_url}
                        controls
                        className="w-full h-64 object-cover rounded-lg"
                        autoPlay
                      />
                      <button
                        onClick={() => setShowVideo(false)}
                        className="absolute top-2 right-2 w-8 h-8 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/70 transition-colors cursor-pointer"
                      >
                        <i className="ri-image-line text-sm"></i>
                      </button>
                    </div>
                  )}
                  
                  {/* Media Toggle Buttons */}
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => setShowVideo(false)}
                      className={`px-4 py-2 rounded-lg text-sm transition-colors cursor-pointer flex items-center gap-2 ${
                        !showVideo ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <i className="ri-image-line"></i>
                      Photos ({uniqueImages.length})
                    </button>
                    {vehicle.video_url && (
                      <button
                        onClick={() => setShowVideo(true)}
                        className={`px-4 py-2 rounded-lg text-sm transition-colors cursor-pointer flex items-center gap-2 ${
                          showVideo ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <i className="ri-play-line"></i>
                        Video
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-3xl font-bold text-blue-600">${vehicle.price.toLocaleString()}</span>
                    {vehicle.mileage && (
                      <span className="text-gray-600">{vehicle.mileage.toLocaleString()} km</span>
                    )}
                  </div>
                  
                  {vehicle.features && vehicle.features.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {vehicle.features.map((feature: string, index: number) => (
                        <span key={index} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                          {feature}
                        </span>
                      ))}
                    </div>
                  )}

                  {vehicle.description && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-2">Description</h4>
                      <p className="text-gray-700 text-sm leading-relaxed">{vehicle.description}</p>
                    </div>
                  )}

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Price Breakdown (Quebec)</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Vehicle Price:</span>
                        <span className="font-semibold">${vehicle.price.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">GST (5%):</span>
                        <span className="font-semibold">${Math.round(gst).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">QST (9.975%):</span>
                        <span className="font-semibold">${Math.round(qst).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between border-t pt-2">
                        <span className="text-gray-900 font-semibold">Total Price:</span>
                        <span className="font-bold text-lg">${Math.round(totalWithTaxes).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Options */}
              <div>
                <h3 className="text-xl font-semibold mb-6">Purchase Options</h3>
                
                {/* Payment Type Selection */}
                <div className="mb-6">
                  <div className="grid grid-cols-1 gap-4">
                    <button
                      onClick={() => setSelectedPaymentType('deposit')}
                      className={`p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                        selectedPaymentType === 'deposit'
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-center">
                        <div className="font-semibold text-gray-900">Standard Deposit</div>
                        <div className="text-2xl font-bold text-blue-600">${Math.round(depositAmount).toLocaleString()}</div>
                        <div className="text-sm text-gray-600">10% down payment</div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => setSelectedPaymentType('custom')}
                      className={`p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                        selectedPaymentType === 'custom'
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-center">
                        <div className="font-semibold text-gray-900">Custom Amount</div>
                        <div className="text-2xl font-bold text-green-600">
                          ${customAmount ? Number(customAmount).toLocaleString() : '0'}
                        </div>
                        <div className="text-sm text-gray-600">Choose any amount you want</div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => setSelectedPaymentType('full')}
                      className={`p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                        selectedPaymentType === 'full'
                          ? 'border-purple-600 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-center">
                        <div className="font-semibold text-gray-900">Full Payment</div>
                        <div className="text-2xl font-bold text-purple-600">${Math.round(totalWithTaxes).toLocaleString()}</div>
                        <div className="text-sm text-gray-600">Complete purchase</div>
                      </div>
                    </button>
                  </div>

                  {/* Custom Amount Input */}
                  {selectedPaymentType === 'custom' && (
                    <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Enter Your Payment Amount
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">$</span>
                        <input
                          type="text"
                          value={customAmount}
                          onChange={handleCustomAmountChange}
                          placeholder="0"
                          className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg font-semibold"
                        />
                      </div>
                      <div className="mt-2 text-sm text-gray-600">
                        <p>• Minimum: $1,000</p>
                        <p>• Maximum: ${Math.round(totalWithTaxes).toLocaleString()} (full price with taxes)</p>
                        <p>• Pay any amount between $1,000 and the full price</p>
                      </div>
                      {customAmount && Number(customAmount) > 0 && (
                        <div className="mt-3 p-3 bg-white rounded border">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Your Payment:</span>
                            <span className="font-bold text-lg text-green-600">
                              ${Number(customAmount).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">Remaining Balance:</span>
                            <span className="font-semibold">
                              ${Math.max(0, Math.round(totalWithTaxes) - Number(customAmount)).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Customer Information */}
                <div className="space-y-4 mb-6">
                  <h4 className="font-semibold text-gray-900">Customer Information</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={customerInfo.firstName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="Enter first name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={customerInfo.lastName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="Enter last name"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={customerInfo.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter email address"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={customerInfo.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter phone number"
                    />
                  </div>
                </div>

                {/* Payment Button */}
                <div className="flex gap-4">
                  <button
                    onClick={onClose}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePayment}
                    disabled={isProcessingPayment || (selectedPaymentType === 'custom' && (!customAmount || Number(customAmount) <= 0))}
                    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                  >
                    {isProcessingPayment ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        Processing...
                      </span>
                    ) : (
                      `Pay $${getPaymentAmount().toLocaleString()}`
                    )}
                  </button>
                </div>

                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-start gap-2 text-blue-800">
                    <i className="ri-information-line mt-1"></i>
                    <div className="text-sm">
                      <p className="font-medium">Flexible Payment Options</p>
                      <p>
                        {selectedPaymentType === 'custom' 
                          ? 'Pay any amount you want - from a small deposit to the full price. You can arrange the remaining balance later.'
                          : selectedPaymentType === 'deposit'
                          ? 'Pay the standard 10% deposit now and arrange the remaining balance later.'
                          : 'Complete your purchase with full payment including Quebec taxes (GST + QST).'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Lightbox */}
      {isLightboxOpen && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4"
          onClick={closeLightbox}
          onKeyDown={handleLightboxKeyDown}
          tabIndex={0}
        >
          <div className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center">
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 w-12 h-12 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/70 transition-colors cursor-pointer z-10"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            {/* Navigation Arrows */}
            {uniqueImages.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage();
                  }}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/70 transition-colors cursor-pointer z-10"
                >
                  <i className="ri-arrow-left-line text-xl"></i>
                </button>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage();
                  }}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/70 transition-colors cursor-pointer z-10"
                >
                  <i className="ri-arrow-right-line text-xl"></i>
                </button>
              </>
            )}

            {/* Main Image */}
            <img 
              src={uniqueImages[selectedImageIndex] || vehicle.image_url || vehicle.image}
              alt={`${vehicle.title} - Image ${selectedImageIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Image Counter */}
            {uniqueImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm">
                {selectedImageIndex + 1} of {uniqueImages.length}
              </div>
            )}

            {/* Thumbnail Navigation */}
            {uniqueImages.length > 1 && (
              <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 flex gap-2 max-w-full overflow-x-auto px-4">
                {uniqueImages.map((imageUrl, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImageIndex(index);
                    }}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors cursor-pointer ${
                      selectedImageIndex === index 
                        ? 'border-white' 
                        : 'border-gray-400 hover:border-gray-200'
                    }`}
                  >
                    <img 
                      src={imageUrl} 
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
